import { useState, useEffect, useRef, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { DialogContent, DialogHeader, Spinner, Typography } from '@airbus/components-react';
import ViewsList from './ViewsList';
import { useUserContextApi } from '../../../context/UserContext';
import { useLanguageContextAPI } from '../../../context/LanguageContext';
import { AllColumnsCheckbox } from './AllColumnsCheckbox';
import { useAlertBar } from '../../../context/AlertBarContext';
import { useDraggableResizableDialog } from './useDraggableResizableDialog';
import { SaveColumnWidthToggle } from './SaveColumnWidthToggle';
import styles from './index.module.css';

const MIN_HEIGHT = 415;

export default function ViewManger(props: {
  apiRef: any;
  open: boolean;
  setOpen: (v: boolean) => void;
}) {
  const { open, setOpen } = props;

  const [orientationTick, setOrientationTick] = useState(0);
  const wasOpenRef = useRef(open);
  const orientationChangedKeyRef = useRef<number | null>(null);
  useEffect(() => {
    wasOpenRef.current = open;
  }, [open]);

  const handleOrientationFrame = (
    orientationChangedKeyRef: { current: number | null },
    wasOpenRef: { current: boolean },
    setOrientationTick: React.Dispatch<React.SetStateAction<number>>,
    setOpen: (v: boolean) => void,
  ) => {
    orientationChangedKeyRef.current = null;

    if (!wasOpenRef.current) return;

    setOrientationTick((t) => t + 1);
    setOpen(true);
  };

  useEffect(() => {    
    const onChange = () => {
      if (orientationChangedKeyRef.current != null) return;

      orientationChangedKeyRef.current = requestAnimationFrame(() =>
        handleOrientationFrame(orientationChangedKeyRef, wasOpenRef, setOrientationTick, setOpen),
      );
    };
    const mq = window.matchMedia('(orientation: portrait)');
    mq.addEventListener?.('change', onChange);
    window.addEventListener('orientationchange', onChange);
    return () => {
      mq.removeEventListener?.('change', onChange);
      window.removeEventListener('orientationchange', onChange);
      if (orientationChangedKeyRef.current != null) {
        cancelAnimationFrame(orientationChangedKeyRef.current);
      }
    };
  }, [setOpen]);

  return open ? <InnerViewManager key={orientationTick} {...props} /> : null;
}

function InnerViewManager(props: { apiRef: any; setOpen: (v: boolean) => void }) {
  const { apiRef, setOpen } = props;

  const { userDetails } = useUserContextApi();
  const { language } = useLanguageContextAPI();
  const { setAlertbar } = useAlertBar();

  const [isUpdateing, setIsUpdateing] = useState(false);
  const [saveWidths, setSaveWidths] = useState(false);

  const screenName = userDetails?.appDetails?.[`screenDisplayName${language}`] ?? '';
  const handleClose = () => setOpen(false);

  const [vw, setVw] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [vh, setVh] = useState(typeof window !== 'undefined' ? window.innerHeight : 768);

  const MIN_WIDTH = vw < 450 ? 338 : 438;

  useEffect(() => {
    const onResize = () => {
      setVw(window.innerWidth);
      setVh(window.innerHeight);
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  const initWidthNow = vw <= 500 ? 340 : 550;

  // Dynamically set INIT_HEIGHT based on screen width
  const initHeightNow = vw >= 320 && vw <= 600 ? 750 : 550;

  const { dlgRect, onHeaderMouseDown, resizeHandles, onResizeMouseDown } =
    useDraggableResizableDialog({
      minWidth: MIN_WIDTH,
      minHeight: MIN_HEIGHT,
      initWidth: initWidthNow,
      initHeight: initHeightNow,
    });

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const GUTTER = 8;
  const TOP_PAD = 4;
  const BOTTOM_PAD = 0;
  const maxW = Math.max(MIN_WIDTH, vw - GUTTER * 2);
  const maxH = Math.max(MIN_HEIGHT, vh - TOP_PAD - BOTTOM_PAD);

  const width = Math.min(dlgRect.width, maxW);
  const height = Math.min(dlgRect.height, maxH);

  const left = Math.max(GUTTER, Math.min(dlgRect.left, vw - GUTTER - width));
  const top = Math.max(TOP_PAD, Math.min(dlgRect.top, vh - BOTTOM_PAD - height));

  const isShortLandscape = vw > vh && vh <= 520;

  const frameStyle = isShortLandscape
    ? {
        left: Math.max(GUTTER, Math.round((vw - width) / 2)),
        top: TOP_PAD,
        bottom: BOTTOM_PAD,
        width,
        minWidth: MIN_WIDTH,
        display: 'flex',
        flexDirection: 'column' as const,
        overflow: 'hidden',
      }
    : {
        left,
        top,
        width,
        height,
        minWidth: MIN_WIDTH,
        minHeight: MIN_HEIGHT,
        maxWidth: maxW,
        maxHeight: maxH,
        display: 'flex',
        flexDirection: 'column' as const,
        overflow: 'hidden',
      };

  return (
    <>
      {createPortal(<div className={styles.backdrop} onClick={handleClose} />, document.body)}

      {createPortal(
        <div role='dialog' aria-modal='true' style={frameStyle} className={styles.headDraggable}>
          <div className={styles.move} onMouseDown={onHeaderMouseDown}>
            <DialogHeader className={styles.headerRow}>
              <Typography variant='h6'>View Manager - {screenName}</Typography>
              <button aria-label='Close' className={styles.close} onClick={handleClose}>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  viewBox='0 0 24 24'
                  fill='#063B9E'
                  width='27'
                  height='27'
                >
                  <path d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z' />
                  <path d='M0 0h24v24H0z' fill='none' />
                </svg>
              </button>
            </DialogHeader>
          </div>

          <DialogContent className={styles.dialogContent}>
            <Suspense fallback={<Spinner />}>
              <ViewsList
                apiRef={apiRef}
                setDefaultBtn
                setAlertbar={setAlertbar}
                isUpdateing={isUpdateing}
                setIsUpdateing={setIsUpdateing}
                showViewActions={true}
                saveWidthsCheckBox={saveWidths}
                setSaveWidths={setSaveWidths}
              />

              {/* Fills remaining height: list card inside will scroll */}
              <div className={styles.contentWrapper}>
                <AllColumnsCheckbox />
                <div className={styles.footer}>
                  <SaveColumnWidthToggle checked={saveWidths} onToggleChange={setSaveWidths} />
                </div>
              </div>
            </Suspense>

            {resizeHandles.map((handle) => (
              <div
                key={handle.key}
                style={{
                  position: 'absolute',
                  zIndex: 99,
                  background: 'transparent',
                  ...handle.style,
                }}
                onMouseDown={(e) => onResizeMouseDown(e, handle.key)}
              />
            ))}
          </DialogContent>
        </div>,
        document.body,
      )}
    </>
  );
}